/* UTILS */
import ViktorClient from '@/api/clients/viktorClient/ViktorClient';
/* CONSTANTS */
import { DEFAULT_PER_PAGE } from '@/api/constants';
/* TYPES */
import type { BatchCallData, BatchResponse } from '@/types/batches/batches.types';

import { postBatch } from '../batches/batches.api';
import type { SharepoolCandidatesResponse } from './sharepool.types';

/**
 * @param {string} uuid - uuid of sharepool
 * @param data
 * @param {number} data.page - page to query
 * @param {string} data.searchTerm - search term
 * @returns {Promise<SharepoolCandidatesResponse>}
 */
export function getSharepoolCandidates(
  uuid,
  {
    page = 1,
    searchTerm,
    statusId = undefined,
    userUuid,
    excludeGroupUuid,
    withPending = false
  }: {
    page: number;
    searchTerm?: string;
    statusId?: number;
    userUuid?: string;
    excludeGroupUuid?: string;
    withPending?: boolean;
  },
  { noOrder = false } = {}
): Promise<SharepoolCandidatesResponse> {
  const params = {
    page,
    per_page: DEFAULT_PER_PAGE,
    fields: 'is_completed,aplayer_score,is_shape_done,is_drive_done,is_brain_done',
    order: !noOrder ? 'status:desc,first_name:asc,last_name:asc,email:asc' : undefined,
    'search[user_data:slug_full_name,email]': searchTerm?.trim() || undefined,
    exclude_group_uuids: excludeGroupUuid ? [excludeGroupUuid] : undefined
  } as Record<string, any>;

  if (statusId) {
    params.status = statusId;
  }

  if (userUuid) {
    params.uuid = userUuid;
  }
  if (withPending) {
    params.with_pending = 1;
  }

  return ViktorClient().get(`/sharepool/${uuid}/candidates`, { params });
}

/**
 * Delete a user from one or multiple sharepools
 *
 * @param {string} userUuid - UUID of the user to remove
 * @param {string[]} sharepoolUuids - List of sharepool UUIDs from which to remove the user
 * @returns {Promise<BatchResponse<void>>}
 */
export function deleteUserFromSharepools(userUuid: string, sharepoolUuids: string[]): Promise<BatchResponse<void>> {
  if (sharepoolUuids.length === 1) {
    return ViktorClient().delete(`/sharepool/${sharepoolUuids[0]}/user/${userUuid}`);
  }

  const data: BatchCallData[] = sharepoolUuids.map(sharepoolUuid => ({
    params: {
      sharepool_uuid: sharepoolUuid,
      user_uuid: userUuid
    }
  }));

  return postBatch({
    action: {
      method: 'DELETE',
      endpoint: '/viktor/sharepool/:sharepool_uuid/user/:user_uuid',
      data
    }
  });
}

/**
 * Add a user to one or multiple sharepools
 *
 * @param {string} userUuid - UUID of the user to add
 * @param {string[]} sharepoolUuids - List of sharepool UUIDs to which the user should be added
 * @returns {Promise<BatchResponse<void>>}
 */
export function addUserToSharepools(userUuid: string, sharepoolUuids: string[]): Promise<BatchResponse<void>> {
  if (sharepoolUuids.length === 1) {
    return ViktorClient().post(`/sharepool/${sharepoolUuids[0]}/user/${userUuid}`);
  }

  const data: BatchCallData[] = sharepoolUuids.map(sharepoolUuid => ({
    params: {
      sharepool_uuid: sharepoolUuid,
      user_uuid: userUuid
    }
  }));

  return postBatch({
    action: {
      method: 'POST',
      endpoint: '/viktor/sharepool/:sharepool_uuid/user/:user_uuid',
      data
    }
  });
}
